import {
	queryCapacityAction,
	rejectQueryCapacityAction,
	resolveQueryCapacityAction,
	returnCapacityAction,
	useCapacityAction,
} from '../actions/capacity.actions';
import {
	call,
	cancel,
	cancelled,
	fork,
	put,
	take,
	ForkEffect,
} from 'redux-saga/effects';
import {
	queryCapacityRequest,
	useCapacityRequest,
	returnCapacityRequest,
} from '../services/capacity';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
// eslint-disable-next-line import/no-unresolved
import { ActionMatchingPattern } from '@redux-saga/types';
import { CapacityItem } from '../utils/capacity';

// TODO:
//
// Define better errors when API calls fail.
// There is a CapacityRequestError object to be implemented and then we can derive error messaging from that.
// Over capacity status SHOULD NOT be handled as an error as far as UI is concerned. }
// We may show warning/error messages but the app itself should not go into an error state.

type QueryCapacityResponse = Awaited<ReturnType<typeof queryCapacityRequest>>;

function takeLatestByCapacityItem<P extends { item: CapacityItem }>(
	pattern: ActionCreatorWithPayload<P>,
	// This is a valid use case of any.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	worker: (action: ActionMatchingPattern<ActionCreatorWithPayload<P>>) => any,
): ForkEffect<never> {
	return fork(function* () {
		const tasks = {};

		while (true) {
			const action = yield take(pattern);
			const item = action.payload.item;

			if (tasks[item]) {
				yield cancel(tasks[item]);
			}

			tasks[item] = yield fork(worker, action);
		}
	});
}

function* watchQueryCapacityAction() {
	yield takeLatestByCapacityItem(queryCapacityAction, function* (action) {
		const controller = new AbortController();
		const { item, resolve, reject } = action.payload;

		try {
			const response: QueryCapacityResponse = yield call(
				queryCapacityRequest,
				{ item },
				controller.signal,
			);

			yield put(
				resolveQueryCapacityAction({ item, data: response.data }),
			);
			yield call(resolve, response.data);
		} catch (error) {
			yield put(rejectQueryCapacityAction({ item, error }));
			yield call(reject, error);
		} finally {
			if (yield cancelled()) {
				controller.abort();
			}
		}
	});
}

function* watchUseCapacityAction() {
	yield takeLatestByCapacityItem(useCapacityAction, function* (action) {
		const controller = new AbortController();
		const { item, quantity, resolve, reject } = action.payload;

		try {
			yield call(
				useCapacityRequest,
				{ item, quantity },
				controller.signal,
			);
		} catch (error) {
			yield call(reject, error);
		} finally {
			if (yield cancelled()) {
				controller.abort();
			} else {
				yield put(queryCapacityAction({ item, resolve, reject }));
			}
		}
	});
}

function* watchReturnCapacityAction() {
	yield takeLatestByCapacityItem(returnCapacityAction, function* (action) {
		const controller = new AbortController();
		const { item, quantity, resolve, reject } = action.payload;

		try {
			yield call(
				returnCapacityRequest,
				{ item, quantity },
				controller.signal,
			);
			yield put(queryCapacityAction({ item, resolve, reject }));
		} catch (error) {
			yield call(reject, error);
		} finally {
			if (yield cancelled()) {
				controller.abort();
			} else {
				yield put(queryCapacityAction({ item, resolve, reject }));
			}
		}
	});
}

export const capacitySagas = [
	watchQueryCapacityAction(),
	watchUseCapacityAction(),
	watchReturnCapacityAction(),
];
